import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Save, X, Package } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { brandSpecsManager, authManager } from '../mock';
import { useToast } from '../hooks/use-toast';
import UserNotesSystem from './UserNotesSystem';

const EditBrandSpecs = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [brandNotFound, setBrandNotFound] = useState(false);

  // Memoized handler for notes changes to prevent infinite loops
  const handleNotesChange = useCallback((notes) => {
    setFormData(prev => ({ ...prev, userNotes: notes }));
  }, []);

  const [formData, setFormData] = useState({
    // Client Details
    brandName: '',
    brandCode: '',
    buyerCompany: '',
    contactPerson: '',
    clientPhone: '',
    clientEmail: '',
    clientAddress: '',
    
    // Bag Details
    bagSizeWidth: '',
    bagSizeLength: '',
    cuttingSizeWidth: '',
    cuttingSizeLength: '',
    top: 'Not Applicable',
    bottom: '',
    bagWeight: '',
    capacity: '',
    
    // PP Woven Fabric Details
    fabricVariant: '',
    fabricColor: '',
    fabricGrams: '',
    
    // BOPP Specifications
    printingMaterial: '',
    boppFilmSize: '',
    boppMicron: '',
    metallic: 'No',
    metallicMicrons: '',
    metallicFilmSize: '',
    window: 'No',
    windowMetallicFilmSize: '',
    
    // Other Details
    lamination: '',
    gusset: 'No',
    handle: 'No',
    perforation: 'No',
    
    // Cylinder Details
    printingAreaWidth: '',
    printingAreaCircum: '',
    cylinderSizeWidth: '',
    cylinderSizeCircum: '',
    numberOfCylinders: '',
    whiteCylinder: 'No',
    commonCylinder: '',
    
    // Artwork
    artworkImage: null,
    artworkImageUrl: '',
    artworkFileName: '',
    artworkFileType: '',
    
    // Status & Notes
    status: 'Active',
    userNotes: []
  });

  useEffect(() => {
    if (id) {
      const brand = brandSpecsManager.getBrandById(parseInt(id));
      if (brand) {
        setFormData(brand);
      } else {
        setBrandNotFound(true);
        toast({
          title: 'Brand Not Found',
          description: 'The requested brand specification could not be found.',
          variant: 'destructive'
        });
      }
    }
  }, [id, toast]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File Too Large',
          description: 'Please select a file smaller than 5MB.',
          variant: 'destructive'
        });
        return;
      }

      // Check file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: 'Invalid File Type',
          description: 'Please select an image (JPG, PNG, GIF) or PDF file.',
          variant: 'destructive'
        });
        return;
      }

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          artworkImage: file,
          artworkImageUrl: event.target.result,
          artworkFileName: file.name,
          artworkFileType: file.type
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Special logic for fabric variant change
      if (field === 'fabricVariant') {
        if (value === 'Natural') {
          // When Natural variant is selected, fabric color must be Natural
          newData.fabricColor = 'Natural';
        } else {
          // When other variants are selected, reset window and remove Natural color lock
          newData.window = 'No';
          newData.windowMetallicFilmSize = '';
        }
      }
      
      // Special logic for metallic change
      if (field === 'metallic' && value === 'No') {
        newData.metallicMicrons = '';
        newData.metallicFilmSize = '';
      }
      
      // Special logic for window change
      if (field === 'window') {
        if (value === 'No') {
          newData.windowMetallicFilmSize = '';
        } else if (value === 'Yes' && newData.metallic === 'No') {
          // When Window is Yes but Metallic is No, automatically set to Not Applicable
          newData.windowMetallicFilmSize = 'Not Applicable';
        }
      }
      
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.brandName || !formData.brandCode || !formData.buyerCompany) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in Brand Name, Brand Code, and Buyer Company.',
        variant: 'destructive'
      });
      return;
    }

    // Bag details validation
    if (!formData.bottom) {
      toast({
        title: 'Validation Error',
        description: 'Please select a Bottom type in Bag Details.',
        variant: 'destructive'
      });
      return;
    }

    // Material specifications validation
    if (!formData.fabricVariant || !formData.fabricColor || !formData.fabricGrams) {
      toast({
        title: 'Validation Error',
        description: 'Please complete all material specifications.',
        variant: 'destructive'
      });
      return;
    }

    // BOPP Details validation
    if (!formData.printingMaterial || !formData.boppMicron || !formData.boppFilmSize) {
      toast({
        title: 'Validation Error',
        description: 'Please complete all BOPP details including BOPP Film Size.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const brandData = {
        ...formData,
        bagSizeWidth: parseFloat(formData.bagSizeWidth) || 0,
        bagSizeLength: parseFloat(formData.bagSizeLength) || 0,
        cuttingSizeWidth: parseFloat(formData.cuttingSizeWidth) || 0,
        cuttingSizeLength: parseFloat(formData.cuttingSizeLength) || 0,
        bagWeight: parseFloat(formData.bagWeight) || 0,
        fabricGrams: parseInt(formData.fabricGrams) || 0,
        // Cylinder details
        printingAreaWidth: parseFloat(formData.printingAreaWidth) || 0,
        printingAreaCircum: parseFloat(formData.printingAreaCircum) || 0,
        cylinderSizeWidth: parseFloat(formData.cylinderSizeWidth) || 0,
        cylinderSizeCircum: parseFloat(formData.cylinderSizeCircum) || 0,
        numberOfCylinders: parseInt(formData.numberOfCylinders) || 0,
        // Artwork - store base64 data URL for now (in production, upload to cloud storage)
        artworkImageUrl: formData.artworkImageUrl || '',
        // Notes system
        userNotes: formData.userNotes || [],
        lastModified: new Date().toISOString(),
        modifiedBy: authManager.getCurrentUser()?.name || 'Unknown User'
      };

      const result = brandSpecsManager.updateBrand(parseInt(id), brandData);
      
      if (result.success) {
        toast({
          title: 'Brand Updated Successfully',
          description: `${brandData.brandName} has been updated in the specifications database.`,
        });

        navigate('/brand-specs');
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update brand specification. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (brandNotFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Brand Not Found</h1>
          <p className="text-gray-600 mb-6">The requested brand specification could not be found.</p>
          <Link to="/brand-specs">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Brand Specs
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Link to="/brand-specs">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Brand Specs
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Brand Specification</h1>
              <p className="text-gray-600">Update BOPP bag specifications for {formData.brandName}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Client Details */}
          <Card>
            <CardHeader>
              <CardTitle>Client Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brandName">Brand Name *</Label>
                  <Input
                    id="brandName"
                    type="text"
                    placeholder="Enter brand name"
                    value={formData.brandName}
                    onChange={(e) => handleInputChange('brandName', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="brandCode">Brand Code *</Label>
                  <Input
                    id="brandCode"
                    type="text"
                    placeholder="Enter brand code"
                    value={formData.brandCode}
                    onChange={(e) => handleInputChange('brandCode', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="buyerCompany">Buyer Company *</Label>
                <Input
                  id="buyerCompany"
                  type="text"
                  placeholder="Enter buyer company name"
                  value={formData.buyerCompany}
                  onChange={(e) => handleInputChange('buyerCompany', e.target.value)}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contactPerson">Contact Person</Label>
                  <Input
                    id="contactPerson"
                    type="text"
                    placeholder="Contact person name"
                    value={formData.contactPerson}
                    onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientPhone">Phone Number</Label>
                  <Input
                    id="clientPhone"
                    type="tel"
                    placeholder="Phone number"
                    value={formData.clientPhone}
                    onChange={(e) => handleInputChange('clientPhone', e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clientEmail">Email Address</Label>
                  <Input
                    id="clientEmail"
                    type="email"
                    placeholder="Email address"
                    value={formData.clientEmail}
                    onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clientAddress">Address</Label>
                  <Input
                    id="clientAddress"
                    type="text"
                    placeholder="Client address"
                    value={formData.clientAddress}
                    onChange={(e) => handleInputChange('clientAddress', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bag Details */}
          <Card>
            <CardHeader>
              <CardTitle>Bag Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Bag Size */}
              <div className="space-y-2">
                <Label className="text-base font-medium">Bag Size (Inches) *</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="bagSizeWidth">Width</Label>
                    <Select value={formData.bagSizeWidth?.toString() || ''} onValueChange={(value) => handleInputChange('bagSizeWidth', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select width" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="12">12</SelectItem>
                        <SelectItem value="14">14</SelectItem>
                        <SelectItem value="14.5">14.5</SelectItem>
                        <SelectItem value="15">15</SelectItem>
                        <SelectItem value="16">16</SelectItem>
                        <SelectItem value="17">17</SelectItem>
                        <SelectItem value="18">18</SelectItem>
                        <SelectItem value="19">19</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="21">21</SelectItem>
                        <SelectItem value="22">22</SelectItem>
                        <SelectItem value="24">24</SelectItem>
                        <SelectItem value="26">26</SelectItem>
                        <SelectItem value="28">28</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="bagSizeLength">Length</Label>
                    <Input
                      id="bagSizeLength"
                      type="number"
                      step="0.1"
                      value={formData.bagSizeLength || ''}
                      onChange={(e) => handleInputChange('bagSizeLength', e.target.value)}
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500">Final bag dimensions: Width × Length</p>
              </div>

              {/* Cutting Size */}
              <div className="space-y-2">
                <Label className="text-base font-medium">Cutting Size (Inches) *</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cuttingSizeWidth">Width</Label>
                    <Input
                      id="cuttingSizeWidth"
                      type="number"
                      step="0.1"
                      value={formData.cuttingSizeWidth || ''}
                      onChange={(e) => handleInputChange('cuttingSizeWidth', e.target.value)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="cuttingSizeLength">Length</Label>
                    <Input
                      id="cuttingSizeLength"
                      type="number"
                      step="0.1"
                      value={formData.cuttingSizeLength || ''}
                      onChange={(e) => handleInputChange('cuttingSizeLength', e.target.value)}
                    />
                  </div>
                </div>
                <p className="text-sm text-gray-500">Fabric cutting dimensions: Width × Length</p>
              </div>

              {/* Top */}
              <div className="space-y-2">
                <Label htmlFor="top">Top *</Label>
                <Select value={formData.top || 'Not Applicable'} onValueChange={(value) => handleInputChange('top', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Not Applicable">Not Applicable</SelectItem>
                    <SelectItem value="Top Hemming">Top Hemming</SelectItem>
                    <SelectItem value="Top Hemming with Handle">Top Hemming with Handle</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Additional Bag Details */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bottom">Bottom *</Label>
                  <Select value={formData.bottom || ''} onValueChange={(value) => handleInputChange('bottom', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select bottom type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single fold single stitch">Single fold single stitch</SelectItem>
                      <SelectItem value="Single fold double stitch">Single fold double stitch</SelectItem>
                      <SelectItem value="Double fold single stitch">Double fold single stitch</SelectItem>
                      <SelectItem value="Double fold double stitch">Double fold double stitch</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bagWeight">Bag Weight (grams)</Label>
                  <Input
                    id="bagWeight"
                    type="number"
                    step="0.1"
                    value={formData.bagWeight || ''}
                    onChange={(e) => handleInputChange('bagWeight', e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="capacity">Capacity (Kg/Ltr)</Label>
                  <Input
                    id="capacity"
                    type="text"
                    value={formData.capacity || ''}
                    onChange={(e) => handleInputChange('capacity', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Material Specifications */}
          <Card>
            <CardHeader>
              <CardTitle>Material Specifications</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* PP Woven Fabric */}
              <div className="space-y-4">
                <Label className="text-base font-medium">PP Woven Fabric Details *</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fabricVariant">Fabric Variant *</Label>
                    <Select value={formData.fabricVariant || ''} onValueChange={(value) => handleInputChange('fabricVariant', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select variant" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Natural">Natural</SelectItem>
                        <SelectItem value="White">White</SelectItem>
                        <SelectItem value="Colored">Colored</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fabricColor">Fabric Color *</Label>
                    <Select 
                      value={formData.fabricColor || ''} 
                      onValueChange={(value) => handleInputChange('fabricColor', value)}
                      disabled={formData.fabricVariant === 'Natural'}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select color" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Natural">Natural</SelectItem>
                        <SelectItem value="White">White</SelectItem>
                        <SelectItem value="Red">Red</SelectItem>
                        <SelectItem value="Blue">Blue</SelectItem>
                        <SelectItem value="Green">Green</SelectItem>
                        <SelectItem value="Yellow">Yellow</SelectItem>
                        <SelectItem value="Black">Black</SelectItem>
                        <SelectItem value="Orange">Orange</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="fabricGrams">Fabric Grams (GSM) *</Label>
                    <Input
                      id="fabricGrams"
                      type="number"
                      value={formData.fabricGrams || ''}
                      onChange={(e) => handleInputChange('fabricGrams', e.target.value)}
                      placeholder="Enter GSM"
                    />
                  </div>
                </div>
              </div>

              {/* BOPP Details */}
              <div className="space-y-4">
                <Label className="text-base font-medium">BOPP Details *</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="printingMaterial">Printing Material *</Label>
                    <Select value={formData.printingMaterial || ''} onValueChange={(value) => handleInputChange('printingMaterial', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select material" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BOPP">BOPP</SelectItem>
                        <SelectItem value="BOPP + OPP">BOPP + OPP</SelectItem>
                        <SelectItem value="BOPP + PE">BOPP + PE</SelectItem>
                        <SelectItem value="BOPP + Metallic">BOPP + Metallic</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="boppMicron">BOPP Micron *</Label>
                    <Select value={formData.boppMicron || ''} onValueChange={(value) => handleInputChange('boppMicron', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select micron" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="18">18 micron</SelectItem>
                        <SelectItem value="20">20 micron</SelectItem>
                        <SelectItem value="22">22 micron</SelectItem>
                        <SelectItem value="25">25 micron</SelectItem>
                        <SelectItem value="30">30 micron</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="boppFilmSize">BOPP Film Size (mm) *</Label>
                    <Input
                      id="boppFilmSize"
                      type="text"
                      value={formData.boppFilmSize || ''}
                      onChange={(e) => handleInputChange('boppFilmSize', e.target.value)}
                      placeholder="Enter film size"
                    />
                  </div>
                </div>

                {/* Metallic Options */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="metallic">Metallic</Label>
                    <Select value={formData.metallic || 'No'} onValueChange={(value) => handleInputChange('metallic', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="No">No</SelectItem>
                        <SelectItem value="Yes">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.metallic === 'Yes' && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="metallicMicrons">Metallic Microns</Label>
                        <Input
                          id="metallicMicrons"
                          type="text"
                          value={formData.metallicMicrons || ''}
                          onChange={(e) => handleInputChange('metallicMicrons', e.target.value)}
                          placeholder="Enter metallic microns"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="metallicFilmSize">Metallic Film Size (mm)</Label>
                        <Input
                          id="metallicFilmSize"
                          type="text"
                          value={formData.metallicFilmSize || ''}
                          onChange={(e) => handleInputChange('metallicFilmSize', e.target.value)}
                          placeholder="Enter metallic film size"
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Window Options */}
                {formData.fabricVariant !== 'Natural' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="window">Window</Label>
                      <Select value={formData.window || 'No'} onValueChange={(value) => handleInputChange('window', value)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="No">No</SelectItem>
                          <SelectItem value="Yes">Yes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {formData.window === 'Yes' && (
                      <div className="space-y-2">
                        <Label htmlFor="windowMetallicFilmSize">Window Metallic Film Size (mm)</Label>
                        <Input
                          id="windowMetallicFilmSize"
                          type="text"
                          value={formData.windowMetallicFilmSize || ''}
                          onChange={(e) => handleInputChange('windowMetallicFilmSize', e.target.value)}
                          placeholder="Enter window film size"
                          readOnly={formData.metallic === 'No'}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Other Options */}
              <div className="space-y-4">
                <Label className="text-base font-medium">Additional Options</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="lamination">Lamination</Label>
                    <Input
                      id="lamination"
                      type="text"
                      value={formData.lamination || ''}
                      onChange={(e) => handleInputChange('lamination', e.target.value)}
                      placeholder="Lamination type"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gusset">Gusset</Label>
                    <Select value={formData.gusset || 'No'} onValueChange={(value) => handleInputChange('gusset', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="No">No</SelectItem>
                        <SelectItem value="Yes">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="handle">Handle</Label>
                    <Select value={formData.handle || 'No'} onValueChange={(value) => handleInputChange('handle', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="No">No</SelectItem>
                        <SelectItem value="Yes">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="perforation">Perforation</Label>
                    <Select value={formData.perforation || 'No'} onValueChange={(value) => handleInputChange('perforation', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="No">No</SelectItem>
                        <SelectItem value="Yes">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Cylinder Details */}
          <Card>
            <CardHeader>
              <CardTitle>Cylinder Details</CardTitle>
              <p className="text-sm text-gray-600">Printing and cylinder specifications (optional)</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="printingAreaWidth">Printing Area Width (mm)</Label>
                  <Input
                    id="printingAreaWidth"
                    type="number"
                    step="0.1"
                    value={formData.printingAreaWidth || ''}
                    onChange={(e) => handleInputChange('printingAreaWidth', e.target.value)}
                    placeholder="Width in mm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="printingAreaCircum">Printing Area Circumference (mm)</Label>
                  <Input
                    id="printingAreaCircum"
                    type="number"
                    step="0.1"
                    value={formData.printingAreaCircum || ''}
                    onChange={(e) => handleInputChange('printingAreaCircum', e.target.value)}
                    placeholder="Circumference in mm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cylinderSizeWidth">Cylinder Size Width (mm)</Label>
                  <Input
                    id="cylinderSizeWidth"
                    type="number"
                    step="0.1"
                    value={formData.cylinderSizeWidth || ''}
                    onChange={(e) => handleInputChange('cylinderSizeWidth', e.target.value)}
                    placeholder="Width in mm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cylinderSizeCircum">Cylinder Size Circumference (mm)</Label>
                  <Input
                    id="cylinderSizeCircum"
                    type="number"
                    step="0.1"
                    value={formData.cylinderSizeCircum || ''}
                    onChange={(e) => handleInputChange('cylinderSizeCircum', e.target.value)}
                    placeholder="Circumference in mm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="numberOfCylinders">Number of Cylinders</Label>
                  <Input
                    id="numberOfCylinders"
                    type="number"
                    min="0"
                    value={formData.numberOfCylinders || ''}
                    onChange={(e) => handleInputChange('numberOfCylinders', e.target.value)}
                    placeholder="Number of cylinders"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="whiteCylinder">White Cylinder</Label>
                  <Select value={formData.whiteCylinder || 'No'} onValueChange={(value) => handleInputChange('whiteCylinder', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="No">No</SelectItem>
                      <SelectItem value="Registered White">Registered White</SelectItem>
                      <SelectItem value="Full Coat White">Full Coat White</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="commonCylinder">Common Cylinder</Label>
                  <Input
                    id="commonCylinder"
                    type="text"
                    value={formData.commonCylinder || ''}
                    onChange={(e) => handleInputChange('commonCylinder', e.target.value)}
                    placeholder="Common cylinder details"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Artwork Upload */}
          <Card>
            <CardHeader>
              <CardTitle>Artwork Upload</CardTitle>
              <p className="text-sm text-gray-600">Upload the artwork for this brand name (optional)</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="artworkImage">Select Artwork File</Label>
                <Input
                  id="artworkImage"
                  type="file"
                  accept="image/*,.pdf"
                  onChange={handleImageUpload}
                  className="cursor-pointer"
                />
                <p className="text-sm text-gray-500">
                  Supported formats: JPG, PNG, GIF, PDF. Max size: 5MB
                </p>
              </div>

              {formData.artworkImageUrl && (
                <div className="mt-4">
                  <Label className="text-sm font-medium">Preview:</Label>
                  <div className="mt-2 p-4 border border-gray-200 rounded-lg bg-gray-50">
                    {formData.artworkFileType === 'application/pdf' ? (
                      <div className="flex items-center space-x-2">
                        <Package className="w-6 h-6 text-red-600" />
                        <span className="text-sm text-gray-700">{formData.artworkFileName}</span>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, artworkImage: null, artworkImageUrl: '', artworkFileName: '', artworkFileType: '' }))}
                          className="text-red-600 hover:text-red-800 text-sm ml-auto"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="relative">
                        <img 
                          src={formData.artworkImageUrl} 
                          alt="Artwork preview" 
                          className="max-w-full max-h-48 object-contain rounded"
                        />
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, artworkImage: null, artworkImageUrl: '', artworkFileName: '', artworkFileType: '' }))}
                          className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-700 text-sm"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Status */}
          <Card>
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(value) => handleInputChange('status', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Additional Details - Notes System */}
          <Card>
            <CardHeader>
              <CardTitle>Additional Details</CardTitle>
              <p className="text-sm text-gray-600">Add notes and comments for this brand specification</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <UserNotesSystem 
                brandId={`brand-${id}`}
                onNotesChange={handleNotesChange}
                initialNotes={formData.userNotes || []}
              />
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Updating Brand...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <Save className="w-4 h-4 mr-2" />
                  Update Brand Specification
                </div>
              )}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => navigate('/brand-specs')}
              disabled={loading}
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditBrandSpecs;